// How the Tasks list lays its rows out, pure (no React) so
// listModel.selftest.ts runs it on its own: what needs you, what is running
// now, then the one-off work that ended by the day it ended (Today,
// Yesterday, Previous 7 days; older by month only in the whole history
// or while searching), and the scheduled runs folded per schedule under
// one Scheduled group with a one-line summary. A scheduled run that needs
// you or is still running is not folded: it stands with the others, where
// the exceptions are.
//
// And the row's words: a schedule's name instead of its slug, who did it
// (and who asked, when another agent did), why it needs you, the time
// short (the group says the day), the result without the agents' NO_REPLY
// marker or a stop's "Cancelled by operator.", and the status only when it
// says something (not Done, not "Working" under Running). And what an
// empty list says: an empty status filter is the usual case, not
// "nothing matches".

import type { TaskQuery, TaskRoundOut, TaskSummary } from "@/api/tasks";
import { MONTHS_LONG, WEEKDAYS, daysBetween, formatDate, formatDuration, formatTime, toDate } from "@/lib/format";
import { routineDisplayName } from "@/lib/routineNames";

import { plainReason } from "../workflows/run/stageState";
import { minutesWords } from "../workflows/time";
import { serverQuery, type TaskFilters } from "./filterModel";
import { approvalLine, askerName, isLiveTask, isNothingToSay, taskGroup, workerName, type TaskGroupKey } from "./taskModel";

type NameOf = (template: string) => string;

/** Started by a schedule (the asker), not by you or an agent. */
export function isScheduled(t: Pick<TaskSummary, "asker">): boolean {
  return t.asker.kind === "schedule";
}

/**
 * A task's title for people: a scheduled run's is its schedule's name in
 * words ("rightmove-cambridge-house-watch" → "Rightmove Cambridge house
 * watch", casing taken from `context`, the runs' own results); any other
 * task's title as it was given.
 */
export function taskTitle(t: Pick<TaskSummary, "asker" | "title" | "conclusion">, context?: string): string {
  if (!isScheduled(t)) return t.title;
  return routineDisplayName(t.title, context ?? t.conclusion ?? "") || t.title;
}

/** A task an agent gave itself (its own work, in a separate session). */
export function isOwnTask(t: Pick<TaskSummary, "asker" | "worker">): boolean {
  return t.worker.kind === "self" && !!t.asker.agent && t.asker.agent === t.worker.template;
}

/** A length of time: "<1 min", "7 min", "2 h 12 min". */
function spent(minutes: number): string {
  if (!Number.isFinite(minutes) || minutes < 1) return "<1 min";
  return formatDuration(Math.floor(minutes) * 60);
}

type Timed = Pick<TaskSummary, "status" | "minutes" | "used_minutes" | "started_at" | "ended_at" | "worker" | "round">;

/**
 * How long a live task has been going, on the clock since it started (the
 * same for every kind of worker: it answers "how long has this been
 * going", a wait on a question included). `since` is when its current
 * round started (the detail knows it); a reopened task without it falls
 * back to the time its turns used. "" when it has not started.
 */
function goingFor(t: Timed, now: number, since?: string | null): string {
  // Queued, it is not running: a reopened one still carries its last round's start.
  if (t.status === "queued") return "";
  const started = Date.parse(since ?? t.started_at ?? "");
  if (!Number.isFinite(started)) return "";
  if (t.round > 0 && !since) return spent(t.used_minutes);
  return spent((now - started) / 60_000);
}

type RoundTimes = readonly (Pick<TaskRoundOut, "started_at" | "ended_at"> & { round?: number })[];

/** Minutes between two timestamps, NaN when either is missing. */
function clockMinutes(from: string | null | undefined, to: string | null | undefined): number {
  return (Date.parse(to ?? "") - Date.parse(from ?? "")) / 60_000;
}

/**
 * How long an ended task took, on the clock from its start to its end
 * ("7 min", "2 h 12 min", "<1 min"), the same for every kind of worker, so
 * a task that read "25 min" while it ran never reads less once it ended.
 * The end is `ended_at`, else its last round's end; a reopened task (the
 * page knows its rounds) adds up each round's own time, not the days
 * between them: the rounds that ended before a reply reopened it, and the
 * current one from `roundStart` (detailModel.roundStartOf) to its end, or
 * the time its turns used. The time its turns used stands in only when
 * the clock cannot be read. "hit 30 min" when it ran out of time; nothing
 * for a task that never started or was stopped within its first minute.
 */
function tookWords(t: Timed, rounds?: RoundTimes, roundStart?: string | null): string {
  if (t.status === "timed_out" && t.minutes > 0) return `hit ${minutesWords(t.minutes)}`;
  let minutes = Number.NaN;
  if (t.round > 0) {
    // Between rounds the task sat ended: the clock from its first start would count that.
    const each = (rounds ?? []).map((r) => clockMinutes(r.started_at, r.ended_at));
    if (each.length && each.every(Number.isFinite)) {
      minutes = each.reduce((a, b) => a + b, 0);
      // The round it is in is not among them until another reply reopens it.
      if (!(rounds ?? []).some((r) => r.round === t.round)) {
        const current = clockMinutes(roundStart, t.ended_at);
        minutes += Number.isFinite(current) && current >= 0 ? current : t.used_minutes;
      }
    }
  } else {
    const lastEnd = (rounds ?? []).map((r) => r.ended_at).filter(Boolean).sort().pop();
    minutes = clockMinutes(t.started_at, t.ended_at ?? lastEnd);
  }
  if (!Number.isFinite(minutes) || minutes < 0) {
    if (!Number.isFinite(Date.parse(t.started_at ?? "")) && !t.used_minutes) return "";
    minutes = t.used_minutes;
  }
  // A task stopped before it got going: no time worth a column.
  if (t.status === "stopped" && minutes < 1) return "";
  return spent(minutes);
}

/**
 * The list's time column, only where it means something. Waiting for your
 * OK: the time it asks for ("3 h asked"), the one fact to decide on. Live:
 * the time on the clock since it started ("25 min", "<1 min"), no limit;
 * nothing before it starts. Ended: how long it took, or "hit 30 min".
 */
export function timeWords(t: Timed, now: number = Date.now()): string {
  if (t.status === "awaiting_approval") return t.minutes > 0 ? `${minutesWords(t.minutes)} asked` : "";
  return isLiveTask(t) ? goingFor(t, now) : tookWords(t);
}

/**
 * The task page's time: "running 25 min · 2 h limit" while it runs (from
 * the start of its current round), the limit alone before it starts (none
 * while it waits for an OK: the approval line says it), and once it ended
 * how long it took (a reopened task's rounds added up, from `rounds`).
 */
export function detailTimeWords(t: Timed, now: number = Date.now(), roundStart?: string | null, rounds?: RoundTimes): string {
  if (!isLiveTask(t)) return tookWords(t, rounds, roundStart);
  const limit = t.minutes > 0 ? `${minutesWords(t.minutes)} limit` : "";
  const going = goingFor(t, now, roundStart);
  if (!going) return t.status === "awaiting_approval" ? "" : limit;
  return [`running ${going}`, limit].filter(Boolean).join(" · ");
}

const STOP_NOTE = /^Cancelled by [\w-]+\.?$/i;

/** Stopped, and all it says is the stop's own note ("Cancelled by operator."). */
export function isStopNoteOnly(t: Pick<TaskSummary, "status" | "conclusion">): boolean {
  return t.status === "stopped" && STOP_NOTE.test(cleanConclusion(t.conclusion));
}
const ENGINE_ENDING = /^work (?:failed|timed out|cancelled):\s*/i;
const RUN_ERROR = /^the run ended with an error:\s*/i;

/**
 * A conclusion without the engine's own prefixes, which only repeat the
 * status beside it: "work failed: the run ended with an error: Python
 * raised MemoryError…" → "Python raised MemoryError…", "work timed out:
 * out of time after 30 min" → "Out of time after 30 min". Only the start
 * of the text is touched.
 */
export function cleanConclusion(conclusion: string | null | undefined): string {
  // the engine's words as a run's page says them ("Python raised MemoryError …" → "It ran out of memory …")
  const text = (conclusion ?? "").trim().replace(ENGINE_ENDING, "").replace(RUN_ERROR, "").trim();
  return /^cancelled by /i.test(text) ? text.charAt(0).toUpperCase() + text.slice(1) : plainReason(text);
}

const LEADING_NO_REPLY = /^NO_REPLY\b[\s:.,;—–-]*/i;
const TRAILING_NO_REPLY = /[\s(]*\bNO_REPLY\)?\.?$/i;

/**
 * What came back, as the list's one quiet line: nothing while it runs or
 * for a stop's own note ("Cancelled by operator."); the agents' NO_REPLY
 * marker (a scheduled run that chose not to send anything) said in words.
 */
export function resultLine(t: Pick<TaskSummary, "status" | "conclusion">): string {
  if (isLiveTask(t)) return "";
  const c = cleanConclusion(t.conclusion);
  if (!c || isStopNoteOnly(t)) return "";
  if (LEADING_NO_REPLY.test(c)) {
    const rest = c.replace(LEADING_NO_REPLY, "").trim();
    return rest ? `Nothing sent: ${rest}` : "Nothing sent";
  }
  return c.replace(TRAILING_NO_REPLY, "").trim() || "Nothing sent";
}

/**
 * The status is said only when it says something: never the usual Done,
 * and not "Working" under the Working heading (Queued, Paused or Waiting
 * for an answer there still say more than the heading).
 */
export function statusShown(t: Pick<TaskSummary, "status">, section?: TaskGroupKey): boolean {
  if (t.status === "finished") return false;
  return !(section === "live" && t.status === "working");
}

// ── the layout ──

export interface ScheduleFold<T> {
  /** The schedule's id (or, for an old run without one, its name). */
  key: string;
  /** The schedule's name in words. */
  name: string;
  /** Its ended runs, newest first. */
  runs: T[];
  latest: T;
}

/** A group of ended one-off work by when it ended: "today", "yesterday", "week", or "month:2026-09". */
export interface DayGroup<T> {
  key: string;
  label: string;
  tasks: T[];
}

/** The Scheduled group's one-line summary: how many schedules, runs that ended today, and of those how many failed. */
export interface ScheduledSummary {
  schedules: number;
  runsToday: number;
  failedToday: number;
}

export interface TaskLayout<T> {
  needsYou: T[];
  running: T[];
  /** One-off work that ended: Today, Yesterday, Previous 7 days, then by month (only with `older`). */
  days: DayGroup<T>[];
  /** Ended one-off work older than a week left out (the whole history shows it). */
  hidden: number;
  schedules: ScheduleFold<T>[];
  scheduled: ScheduledSummary;
}

/** When a task ended (else when it started, else when it was given): what the day groups go by. */
export function endedAt(t: Pick<TaskSummary, "ended_at" | "started_at" | "created_at">): string {
  return t.ended_at || t.started_at || t.created_at;
}

/** The day group a time falls in, from `now`: today, yesterday, the six days before, else its month. */
export function dayGroupKey(at: string, now: Date): string {
  const d = toDate(at);
  if (!d) return "week";
  const days = daysBetween(d, now);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return "week";
  return `month:${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/** A day group's heading: "Today", "Yesterday", "Previous 7 days" (the five days before), "Earlier this month", "September", "December 2025". */
export function dayGroupLabel(key: string, now: Date): string {
  if (key === "today") return "Today";
  if (key === "yesterday") return "Yesterday";
  if (key === "week") return "Previous 7 days";
  const [y, m] = key.slice("month:".length).split("-").map(Number);
  if (y === now.getFullYear() && m === now.getMonth() + 1) return "Earlier this month";
  return `${MONTHS_LONG[m - 1] ?? ""}${y === now.getFullYear() ? "" : ` ${y}`}`.trim();
}

const RECENT = new Set(["today", "yesterday", "week"]);

/**
 * The list's groups. `oneOff` is the one-off read (every task but a
 * schedule's runs that ended with nothing waiting), `scheduled` a
 * schedule's runs (read apart); a task in both counts once. Needs you and
 * Running keep the list's order (newest first); ended one-off work goes by
 * the day it ended, newest first, and older than a week only with `older`
 * (else it is counted in `hidden`). A schedule's ended runs fold into one
 * row per schedule, in the order of their latest run; a scheduled run that
 * needs you or is running stands with the others.
 */
export function layoutTasks<T extends TaskSummary>(oneOff: readonly T[], scheduled: readonly T[] = [], { now = new Date(), older = false }: { now?: Date; older?: boolean } = {}): TaskLayout<T> {
  const out: TaskLayout<T> = { needsYou: [], running: [], days: [], hidden: 0, schedules: [], scheduled: { schedules: 0, runsToday: 0, failedToday: 0 } };
  const seen = new Set<string>();
  const ended: T[] = [];
  const folds = new Map<string, T[]>();
  for (const t of [...oneOff, ...scheduled]) {
    if (seen.has(t.task_id)) continue;
    seen.add(t.task_id);
    const g = taskGroup(t);
    if (g === "needs_you") out.needsYou.push(t);
    else if (g === "live") out.running.push(t);
    else if (isScheduled(t)) {
      const key = t.asker.schedule_id || `name:${t.title}`;
      const runs = folds.get(key);
      if (runs) runs.push(t);
      else folds.set(key, [t]);
      if (dayGroupKey(endedAt(t), now) === "today") {
        out.scheduled.runsToday += 1;
        if (t.status === "failed" || t.status === "timed_out") out.scheduled.failedToday += 1;
      }
    } else ended.push(t);
  }
  const groups = new Map<string, T[]>();
  for (const t of [...ended].sort((a, b) => endedAt(b).localeCompare(endedAt(a)))) {
    const key = dayGroupKey(endedAt(t), now);
    if (!older && !RECENT.has(key)) {
      out.hidden += 1;
      continue;
    }
    const list = groups.get(key);
    if (list) list.push(t);
    else groups.set(key, [t]);
  }
  const order = (k: string) => (k === "today" ? "3" : k === "yesterday" ? "2" : k === "week" ? "1" : `0${k}`);
  out.days = [...groups.keys()].sort((a, b) => order(b).localeCompare(order(a))).map((key) => ({ key, label: dayGroupLabel(key, now), tasks: groups.get(key)! }));
  for (const [key, runs] of folds) {
    const latest = runs[0];
    const context = runs.map((r) => r.conclusion ?? "").join(" \n");
    out.schedules.push({ key, name: taskTitle(latest, context), runs, latest });
  }
  out.scheduled.schedules = out.schedules.length;
  return out;
}

/** "23 runs today", "1 failed": the Scheduled group's summary beside its count of schedules while it is folded. */
export function scheduledWords(s: ScheduledSummary): { text: string; failed: string } {
  return { text: s.runsToday ? `${runsWords(s.runsToday)} today` : "No runs today", failed: s.failedToday ? `${s.failedToday} failed` : "" };
}

/** How many of a schedule's runs show when it is opened, before "Show all". */
export const RUNS_SHOWN = 8;

/**
 * A schedule's runs as its open row shows them: the ones that said
 * something or went wrong (at most RUNS_SHOWN), the quiet ones (nothing to
 * say) folded; `all` shows every run. `more` is the button's words ("" =
 * none), `note` a line beside it when nothing is shown ("Nothing to say in
 * its 85 runs.").
 */
export function runFold<T extends TaskSummary>(runs: readonly T[], all: boolean): { shown: T[]; more: string; note: string } {
  if (all) return { shown: [...runs], more: "", note: "" };
  const loud = runs.filter((r) => !isNothingToSay(r));
  const shown = loud.slice(0, RUNS_SHOWN);
  const quiet = runs.length - loud.length;
  if (shown.length === runs.length) return { shown, more: "", note: "" };
  const note = shown.length ? "" : `Nothing to say in ${runs.length === 1 ? "its run" : `any of its ${runs.length} runs`}.`;
  const more = quiet && shown.length ? `Show all ${runs.length} runs (${quiet} with nothing to say)` : `Show all ${runs.length} runs`;
  return { shown, more, note };
}

/** "1 run", "32 runs". */
export function runsWords(n: number): string {
  return n === 1 ? "1 run" : `${n} runs`;
}

// ── a row's words ──

/**
 * Who on a row: the worker by name, and who asked when another agent did
 * ("Julia" → "Charlotte"); nothing for you, a schedule, an agent's own task.
 * A workflow named like the task itself is just "Workflow".
 */
export function rowWho(t: Pick<TaskSummary, "asker" | "worker" | "title">, nameOf: NameOf): { name: string; from: string } {
  const named = workerName(t.worker, nameOf);
  const name = t.worker.kind === "workflow" && named.trim().toLowerCase() === t.title.trim().toLowerCase() ? "Workflow" : named;
  const from = t.asker.kind === "agent" && !isOwnTask(t) && t.asker.agent !== t.worker.template ? askerName(t.asker, nameOf) : "";
  return { name, from };
}

/**
 * What a row says after its title: who wants it and for how long while it
 * waits for your OK ("Julia asked for 3 h"), nothing while it runs (its
 * status word says enough), else the first words of what came back.
 */
export function rowNote(t: Pick<TaskSummary, "status" | "conclusion" | "asker" | "minutes">, nameOf: NameOf): string {
  if (t.status === "awaiting_approval") return approvalLine(t, nameOf);
  return resultLine(t);
}

/**
 * A row's time, short: "13:02" today; "Yesterday" (or its time inside the
 * Yesterday group); "Tue" (or "Tue 10:16" inside a day group) this week;
 * "27 Oct" before; "27 Oct 2025" in another year. The tooltip has it whole.
 */
export function shortWhen(at: string, now: Date, { inDayGroup = false }: { inDayGroup?: boolean } = {}): string {
  const d = toDate(at);
  if (!d) return "";
  const days = daysBetween(d, now);
  if (days <= 0) return formatTime(d);
  if (days === 1) return inDayGroup ? formatTime(d) : "Yesterday";
  if (days < 7) return inDayGroup ? `${WEEKDAYS[d.getDay()]} ${formatTime(d)}` : WEEKDAYS[d.getDay()];
  return formatDate(d, { weekday: false }, now);
}

// ── the chips a link brings ──

export interface FilterChip {
  key: "worker" | "asker" | "kind";
  label: string;
}

/** Filters only a link sets (the bar has no control for them), as removable chips ("Done by Soren", "Asked by you", "Scheduled", "Questions"). */
export function filterChips(f: Pick<TaskFilters, "worker" | "asker" | "kind">, nameOf: NameOf): FilterChip[] {
  const chips: FilterChip[] = [];
  if (f.worker) chips.push({ key: "worker", label: `Done by ${f.worker === "team" ? "the Team" : nameOf(f.worker) || f.worker}` });
  if (f.asker) {
    const label = f.asker === "operator" ? "Asked by you" : f.asker === "schedule" ? "Scheduled" : `Asked by ${nameOf(f.asker) || f.asker}`;
    chips.push({ key: "asker", label });
  }
  if (f.kind) chips.push({ key: "kind", label: f.kind === "question" ? "Questions" : "Tasks, not questions" });
  return chips;
}

// ── one agent's tasks (its Tasks tab) ──

/** A task this agent gave or is doing (the server's own rule, `involves`). */
export function involvesAgent(t: Pick<TaskSummary, "asker" | "worker">, agent: string): boolean {
  return !!agent && (t.asker.agent === agent || t.worker.template === agent);
}

/** An agent's tab keeps the status filter, the search and the whole history from the URL; who and the kind are fixed. */
export function agentFilters(f: TaskFilters): TaskFilters {
  return { ...f, who: "", worker: "", asker: "", kind: "" };
}

/**
 * What an agent's tab asks the server: its own tasks, without a schedule's
 * runs that ended with nothing waiting (those are under its Schedules tab).
 */
export function agentQuery(f: TaskFilters, agent: string): TaskQuery {
  return { ...serverQuery(agentFilters(f)), agent, includeEndedScheduled: false };
}

// ── when nothing shows ──

export interface EmptyWords {
  title: string;
  description: string;
  /** "clear": the filters hide everything; "all": a status filter with nothing in it, the rest is a click away. */
  action: "clear" | "all" | null;
}

/**
 * What an empty list says. A status filter with nothing in it is the usual
 * case (most of the time nothing needs you): it says so, not "nothing
 * matches". Under an agent: a title and the way back, no help lines.
 */
export function emptyWords(f: TaskFilters, { agent = false } = {}): EmptyWords {
  const narrowed = !!(f.q.trim() || f.who || f.worker || f.asker || f.kind);
  const help = (text: string) => (agent ? "" : text);
  if (narrowed) return { title: "Nothing matches", description: help("No task with these filters."), action: "clear" };
  if (f.view === "needs_you") return { title: "Nothing needs you", description: help("A task shows here when it waits for your OK or its answer did not arrive."), action: "all" };
  if (f.view === "live") return { title: "Nothing is running right now", description: "", action: "all" };
  if (f.view === "ended") return { title: "Nothing has ended yet", description: "", action: "all" };
  if (f.status.length) return { title: "Nothing matches", description: "", action: "all" };
  return { title: "No tasks yet", description: help("Give one to an agent or a subagent. Tasks the agents give each other show here too."), action: null };
}
